import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import { PublicChampionshipLayout } from "@/components/PublicChampionshipLayout";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";

import { lazy, Suspense, ReactNode } from "react";

const PublicChampionshipLayoutWrapper = ({ children }: { children: ReactNode }) => (
  <PublicChampionshipLayout>{children}</PublicChampionshipLayout>
);

const lazyRetry = (importFn: () => Promise<any>) =>
  lazy(() =>
    importFn().catch(() => {
      // Force reload on chunk load failure (stale cache)
      window.location.reload();
      return new Promise(() => {}); // never resolves, page will reload
    })
  );

const Login = lazyRetry(() => import("./pages/Login"));
const Signup = lazyRetry(() => import("./pages/Signup"));
const ForgotPassword = lazyRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const ChampionshipDetail = lazyRetry(() => import("./pages/ChampionshipDetail"));
const ChampionshipTeams = lazyRetry(() => import("./pages/ChampionshipTeams"));
const TeamCreate = lazyRetry(() => import("./pages/TeamCreate"));
const TeamDetail = lazyRetry(() => import("./pages/TeamDetail"));
const ChampionshipPlayers = lazyRetry(() => import("./pages/ChampionshipPlayers"));
const ChampionshipStats = lazyRetry(() => import("./pages/ChampionshipStats"));
const ChampionshipStatsDetail = lazyRetry(() => import("./pages/ChampionshipStatsDetail"));
const PlayerDetail = lazyRetry(() => import("./pages/PlayerDetail"));
const ChampionshipSchedule = lazyRetry(() => import("./pages/ChampionshipSchedule"));
const ChampionshipSettings = lazyRetry(() => import("./pages/ChampionshipSettings"));
const ChampionshipUsers = lazyRetry(() => import("./pages/ChampionshipUsers"));
const ChampionshipReferees = lazyRetry(() => import("./pages/ChampionshipReferees"));
const ChampionshipSocialMedia = lazyRetry(() => import("./pages/ChampionshipSocialMedia"));
const ChampionshipVenues = lazyRetry(() => import("./pages/ChampionshipVenues"));
const MatchDetail = lazyRetry(() => import("./pages/MatchDetail"));
const MatchResultEdit = lazyRetry(() => import("./pages/MatchResultEdit"));
const Profile = lazyRetry(() => import("./pages/Profile"));
const Game = lazyRetry(() => import("./pages/Game"));
const DigitalSumula = lazyRetry(() => import("./pages/DigitalSumula"));
const ManualSumula = lazyRetry(() => import("./pages/ManualSumula"));
const MultiDeviceSumula = lazyRetry(() => import("./pages/MultiDeviceSumula"));
const TeamsManagement = lazyRetry(() => import("./pages/TeamsManagement"));
const GameHistory = lazyRetry(() => import("./pages/GameHistory"));
const AdminSettings = lazyRetry(() => import("./pages/AdminSettings"));
const AdminSumulaTemplates = lazyRetry(() => import("./pages/AdminSumulaTemplates"));
const AdminChampionshipTypes = lazyRetry(() => import("./pages/AdminChampionshipTypes"));
const AdminSportTypes = lazyRetry(() => import("./pages/AdminSportTypes"));
const AdminBranding = lazyRetry(() => import("./pages/AdminBranding"));
const AdminInstitutional = lazyRetry(() => import("./pages/AdminInstitutional"));
const AdminDashboard = lazyRetry(() => import("./pages/AdminDashboard"));
const AdminChampionships = lazyRetry(() => import("./pages/AdminChampionships"));
const AdminPlans = lazyRetry(() => import("./pages/AdminPlans"));
const AdminLandingPage = lazyRetry(() => import("./pages/AdminLandingPage"));
const AdminTickets = lazyRetry(() => import("./pages/AdminTickets"));
const AdminEmailTemplates = lazyRetry(() => import("./pages/AdminEmailTemplates"));
const AdminAds = lazyRetry(() => import("./pages/AdminAds"));
const AdminDemoChampionships = lazyRetry(() => import("./pages/AdminDemoChampionships"));
const ChampionshipAllMatches = lazyRetry(() => import("./pages/ChampionshipAllMatches"));
const ChampionshipStandings = lazyRetry(() => import("./pages/ChampionshipStandings"));
const StandingsExtraEdit = lazyRetry(() => import("./pages/StandingsExtraEdit"));
const ChampionshipFinancial = lazyRetry(() => import("./pages/ChampionshipFinancial"));
const ChampionshipBarbecue = lazyRetry(() => import("./pages/ChampionshipBarbecue"));
const BarbecueShoppingList = lazyRetry(() => import("./pages/BarbecueShoppingList"));
const ChampionshipRoundSelection = lazyRetry(() => import("./pages/ChampionshipRoundSelection"));
const ChampionshipSponsors = lazyRetry(() => import("./pages/ChampionshipSponsors"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const LandingPage = lazyRetry(() => import("./pages/LandingPage"));
const Unsubscribe = lazyRetry(() => import("./pages/Unsubscribe"));
const Checkout = lazyRetry(() => import("./pages/Checkout"));
const DemoChampionships = lazyRetry(() => import("./pages/DemoChampionships"));
const AdminBlog = lazyRetry(() => import("./pages/AdminBlog"));
const Blog = lazyRetry(() => import("./pages/public/Blog"));
const BlogPost = lazyRetry(() => import("./pages/public/BlogPost"));
const AboutSportControl = lazyRetry(() => import("./pages/AboutSportControl"));

// Demo championship ID (Copa Teste)
const DEMO_CHAMPIONSHIP_ID = "ff81ab73-d5eb-4243-99c4-cfcab682c0da";

// Public pages
const PublicOverview = lazyRetry(() => import("./pages/public/PublicOverview"));
const PublicTeams = lazyRetry(() => import("./pages/public/PublicTeams"));
const PublicPlayers = lazyRetry(() => import("./pages/public/PublicPlayers"));
const PublicStats = lazyRetry(() => import("./pages/public/PublicStats"));
const PublicSchedule = lazyRetry(() => import("./pages/public/PublicSchedule"));
const PublicTeamDetail = lazyRetry(() => import("./pages/public/PublicTeamDetail"));
const PublicPlayerDetail = lazyRetry(() => import("./pages/public/PublicPlayerDetail"));
const PublicMatchDetail = lazyRetry(() => import("./pages/public/PublicMatchDetail"));
const PublicStatsDetail = lazyRetry(() => import("./pages/public/PublicStatsDetail"));
const PublicAllMatches = lazyRetry(() => import("./pages/public/PublicAllMatches"));
const PublicPlayerRegistration = lazyRetry(() => import("./pages/public/PublicPlayerRegistration"));
const PublicStandings = lazyRetry(() => import("./pages/public/PublicStandings"));
const PublicBarbecueRegistration = lazyRetry(() => import("./pages/public/PublicBarbecueRegistration"));

const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <p className="text-muted-foreground">Carregando...</p>
  </div>
);

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><p className="text-muted-foreground">Carregando...</p></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><p className="text-muted-foreground">Carregando...</p></div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AnalyticsTracker />
          <AuthProvider>
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/championships/:id" element={<ProtectedRoute><ChampionshipDetail /></ProtectedRoute>} />
              <Route path="/championships/:id/teams" element={<ProtectedRoute><ChampionshipTeams /></ProtectedRoute>} />
              <Route path="/championships/:id/teams/new" element={<ProtectedRoute><TeamCreate /></ProtectedRoute>} />
              <Route path="/championships/:id/teams/import" element={<ProtectedRoute><TeamCreate /></ProtectedRoute>} />
              <Route path="/championships/:id/teams/:teamId" element={<ProtectedRoute><TeamDetail /></ProtectedRoute>} />
              <Route path="/championships/:id/teams/:teamId/edit" element={<ProtectedRoute><TeamCreate /></ProtectedRoute>} />
              <Route path="/championships/:id/schedule" element={<ProtectedRoute><ChampionshipSchedule /></ProtectedRoute>} />
              <Route path="/championships/:id/settings" element={<ProtectedRoute><ChampionshipSettings /></ProtectedRoute>} />
              <Route path="/championships/:id/users" element={<ProtectedRoute><ChampionshipUsers /></ProtectedRoute>} />
              <Route path="/championships/:id/venues" element={<ProtectedRoute><ChampionshipVenues /></ProtectedRoute>} />
              <Route path="/championships/:id/referees" element={<ProtectedRoute><ChampionshipReferees /></ProtectedRoute>} />
              <Route path="/championships/:id/social" element={<ProtectedRoute><ChampionshipSocialMedia /></ProtectedRoute>} />
              <Route path="/championships/:id/matches/:matchId" element={<ProtectedRoute><MatchDetail /></ProtectedRoute>} />
              <Route path="/championships/:id/all-matches" element={<ProtectedRoute><ChampionshipAllMatches /></ProtectedRoute>} />
              <Route path="/championships/:id/standings" element={<ProtectedRoute><ChampionshipStandings /></ProtectedRoute>} />
              <Route path="/championships/:id/standings/edit" element={<ProtectedRoute><StandingsExtraEdit /></ProtectedRoute>} />
              <Route path="/championships/:id/matches/:matchId/edit" element={<ProtectedRoute><MatchResultEdit /></ProtectedRoute>} />
              <Route path="/championships/:id/players" element={<ProtectedRoute><ChampionshipPlayers /></ProtectedRoute>} />
              <Route path="/championships/:id/players/:playerId" element={<ProtectedRoute><PlayerDetail /></ProtectedRoute>} />
              <Route path="/championships/:id/stats" element={<ProtectedRoute><ChampionshipStats /></ProtectedRoute>} />
              <Route path="/championships/:id/stats/:stat" element={<ProtectedRoute><ChampionshipStatsDetail /></ProtectedRoute>} />
              <Route path="/championships/:id/financial" element={<ProtectedRoute><ChampionshipFinancial /></ProtectedRoute>} />
              <Route path="/championships/:id/barbecue" element={<ProtectedRoute><ChampionshipBarbecue /></ProtectedRoute>} />
              <Route path="/championships/:id/barbecue/shopping-list" element={<ProtectedRoute><BarbecueShoppingList /></ProtectedRoute>} />
              <Route path="/championships/:id/round-selection" element={<ProtectedRoute><ChampionshipRoundSelection /></ProtectedRoute>} />
              <Route path="/championships/:id/sponsors" element={<ProtectedRoute><ChampionshipSponsors /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
              <Route path="/game" element={<ProtectedRoute><Game /></ProtectedRoute>} />
              <Route path="/manual-sumula" element={<ProtectedRoute><ManualSumula /></ProtectedRoute>} />
              <Route path="/championships/:id/manual-sumula" element={<ProtectedRoute><ManualSumula /></ProtectedRoute>} />
              <Route path="/championships/:id/digital-sumula" element={<ProtectedRoute><DigitalSumula /></ProtectedRoute>} />
              <Route path="/championships/:id/multi-device-sumula" element={<ProtectedRoute><MultiDeviceSumula /></ProtectedRoute>} />
              <Route path="/teams" element={<ProtectedRoute><TeamsManagement /></ProtectedRoute>} />
              <Route path="/history" element={<ProtectedRoute><GameHistory /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
              <Route path="/admin/sumulas" element={<ProtectedRoute><AdminSumulaTemplates /></ProtectedRoute>} />
              <Route path="/admin/championship-types" element={<ProtectedRoute><AdminChampionshipTypes /></ProtectedRoute>} />
              <Route path="/admin/sports" element={<ProtectedRoute><AdminSportTypes /></ProtectedRoute>} />
              <Route path="/admin/branding" element={<ProtectedRoute><AdminBranding /></ProtectedRoute>} />
              <Route path="/admin/institutional" element={<ProtectedRoute><AdminInstitutional /></ProtectedRoute>} />
              <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/championships" element={<ProtectedRoute><AdminChampionships /></ProtectedRoute>} />
              <Route path="/admin/landing" element={<ProtectedRoute><AdminLandingPage /></ProtectedRoute>} />
              <Route path="/admin/plans" element={<ProtectedRoute><AdminPlans /></ProtectedRoute>} />
              <Route path="/admin/tickets" element={<ProtectedRoute><AdminTickets /></ProtectedRoute>} />
              <Route path="/admin/emails" element={<ProtectedRoute><AdminEmailTemplates /></ProtectedRoute>} />
              <Route path="/admin/ads" element={<ProtectedRoute><AdminAds /></ProtectedRoute>} />
              <Route path="/admin/demo-championships" element={<ProtectedRoute><AdminDemoChampionships /></ProtectedRoute>} />
              <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />

              {/* Public Blog */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />

              {/* Public championship routes */}
              <Route path="/c/:slug" element={<PublicChampionshipLayoutWrapper><PublicOverview /></PublicChampionshipLayoutWrapper>} />
              <Route path="/c/:slug/teams" element={<PublicChampionshipLayoutWrapper><PublicTeams /></PublicChampionshipLayoutWrapper>} />
              <Route path="/c/:slug/teams/:teamId" element={<PublicChampionshipLayoutWrapper><PublicTeamDetail /></PublicChampionshipLayoutWrapper>} />
              {/* Players accessed via team detail, no standalone route */}
              <Route path="/c/:slug/players/:playerId" element={<PublicChampionshipLayoutWrapper><PublicPlayerDetail /></PublicChampionshipLayoutWrapper>} />
              <Route path="/c/:slug/stats" element={<PublicChampionshipLayoutWrapper><PublicStats /></PublicChampionshipLayoutWrapper>} />
              <Route path="/c/:slug/stats/:stat" element={<PublicChampionshipLayoutWrapper><PublicStatsDetail /></PublicChampionshipLayoutWrapper>} />
              <Route path="/c/:slug/schedule" element={<PublicChampionshipLayoutWrapper><PublicSchedule /></PublicChampionshipLayoutWrapper>} />
              <Route path="/c/:slug/all-matches" element={<PublicChampionshipLayoutWrapper><PublicAllMatches /></PublicChampionshipLayoutWrapper>} />
              <Route path="/c/:slug/matches/:matchId" element={<PublicChampionshipLayoutWrapper><PublicMatchDetail /></PublicChampionshipLayoutWrapper>} />
              <Route path="/c/:slug/standings" element={<PublicChampionshipLayoutWrapper><PublicStandings /></PublicChampionshipLayoutWrapper>} />
              <Route path="/c/:slug/social" element={<PublicChampionshipLayoutWrapper><ChampionshipSocialMedia publicMode /></PublicChampionshipLayoutWrapper>} />

              {/* Public player registration */}
              <Route path="/register/:championshipId" element={<PublicPlayerRegistration />} />

              {/* Public barbecue registration */}
              <Route path="/barbecue/:token" element={<PublicBarbecueRegistration />} />

              {/* Demo championship routes (public, no auth) - reuses real page components */}
              <Route path="/demo" element={<DemoChampionships />} />
              <Route path="/demo/championships/:id" element={<ChampionshipDetail />} />
              <Route path="/demo/championships/:id/teams" element={<ChampionshipTeams />} />
              <Route path="/demo/championships/:id/teams/:teamId" element={<TeamDetail />} />
              <Route path="/demo/championships/:id/players" element={<ChampionshipPlayers />} />
              <Route path="/demo/championships/:id/players/:playerId" element={<PlayerDetail />} />
              <Route path="/demo/championships/:id/stats" element={<ChampionshipStats />} />
              <Route path="/demo/championships/:id/stats/:stat" element={<ChampionshipStatsDetail />} />
              <Route path="/demo/championships/:id/schedule" element={<ChampionshipSchedule />} />
              <Route path="/demo/championships/:id/venues" element={<ChampionshipVenues />} />
              <Route path="/demo/championships/:id/referees" element={<ChampionshipReferees />} />
              <Route path="/demo/championships/:id/social" element={<ChampionshipSocialMedia />} />
              <Route path="/demo/championships/:id/round-selection" element={<ChampionshipRoundSelection />} />
              <Route path="/demo/championships/:id/sponsors" element={<ChampionshipSponsors />} />
              <Route path="/demo/championships/:id/financial" element={<ChampionshipFinancial />} />
              <Route path="/demo/championships/:id/barbecue" element={<ChampionshipBarbecue />} />
              <Route path="/demo/championships/:id/barbecue/shopping-list" element={<BarbecueShoppingList />} />
              <Route path="/demo/championships/:id/users" element={<ChampionshipUsers />} />
              <Route path="/demo/championships/:id/settings" element={<ChampionshipSettings />} />
              <Route path="/demo/championships/:id/matches/:matchId" element={<MatchDetail />} />
              <Route path="/demo/championships/:id/all-matches" element={<ChampionshipAllMatches />} />
              <Route path="/demo/championships/:id/standings" element={<ChampionshipStandings />} />

              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/about" element={<AboutSportControl />} />
              <Route path="/" element={<LandingPage />} />
              <Route path="/landing" element={<LandingPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
